import { useMutation } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { AlertDialogDescription } from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { UPSERT_MEDIA_POLICIES } from '@optools/services/operator-gateway/esni/mutations';
import { GET_MEDIA_POLICIES } from '@optools/services/operator-gateway/esni/queries';
import {
  MediaPoliciesQuery,
  MediaPolicy,
  MediaPolicyInput,
  ViewingPolicy,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { ColumnDef } from '@tanstack/react-table';
import { CircleX } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { useDetailsParams } from '../hooks/useDetailsParams';
import { AddPolicyDialog } from './add-policy-dialog';

export interface PoliciesTableProps {
  mediaPolicy: MediaPolicy;
}

export function PoliciesTable({ mediaPolicy }: PoliciesTableProps) {
  const { setDetailsParam } = useDetailsParams();

  const [upsertMediaPolicies, { loading }] = useMutation(UPSERT_MEDIA_POLICIES, {
    update: (cache, { data }) => {
      if (!data?.upsertMediaPolicies) return;

      const upsertPolicy = data.upsertMediaPolicies.find((item) => item?.id === mediaPolicy.id);

      if (!upsertPolicy) return;

      const query = cache.readQuery<MediaPoliciesQuery>({ query: GET_MEDIA_POLICIES });

      if (!query) return;

      const updatedPolicies = query.mediaPolicies.map((policy) => {
        return policy.id === upsertPolicy.id && upsertPolicy ? upsertPolicy : policy;
      });

      cache.writeQuery<MediaPoliciesQuery>({
        data: { mediaPolicies: updatedPolicies },
        query: GET_MEDIA_POLICIES,
      });
    },
  });

  const handleOnConfirm = useCallback(
    (policyHref: string) => {
      const { contentDate, feedSubType, feedType, gamePk, id, language, mediaType, requiredAuth } =
        mediaPolicy;

      const viewingPolicyHrefs = mediaPolicy.viewingPolicies
        .map((item) => item.href)
        .filter((href) => href !== policyHref);

      const mediaPolicyInput: MediaPolicyInput = {
        contentDate,
        feedSubType,
        feedType,
        gamePk,
        id,
        language,
        mediaType,
        requiredAuth,
        viewingPolicyHrefs,
      };

      upsertMediaPolicies({
        onCompleted: ({ upsertMediaPolicies }) => {
          const title = 'Viewing Policy Detached';

          if (!upsertMediaPolicies) {
            toast.error(title, {
              description: `Could not detach viewing policy from media ${id}`,
              duration: Infinity,
            });
            return;
          }

          const upsertPolicy = upsertMediaPolicies.find((item) => item?.id === id);

          if (!upsertPolicy) {
            toast.warning(title, {
              description: `Modified policy is not present in response for media ${id}`,
            });
            return;
          }

          toast.success(title, {
            description: `Detached viewing policy ${policyHref} from media ${id}`,
          });
        },
        variables: { media: mediaPolicyInput },
      });
    },
    [mediaPolicy, upsertMediaPolicies],
  );

  const columns = useMemo<ColumnDef<ViewingPolicy>[]>(
    () => [
      {
        cell: ({ cell }) => {
          const policyHref = cell.row.original.href;
          return (
            <div className="flex items-center justify-start">
              <ConfirmationDialog
                description={
                  <AlertDialogDescription>
                    Are you sure your want to remove{' '}
                    <strong className="font-bold">{policyHref}</strong> from this media?
                  </AlertDialogDescription>
                }
                title="Remove Policy"
                onConfirm={() => {
                  handleOnConfirm(policyHref);
                }}>
                <Button
                  aria-label="Remove Policy"
                  disabled={loading}
                  size="icon"
                  title="Remove Policy"
                  variant="outline">
                  <CircleX />
                </Button>
              </ConfirmationDialog>
            </div>
          );
        },
        header: '',
        id: 'remove',
      },
      {
        accessorKey: 'name',
        cell: ({ cell }) => {
          const name = cell.row.original.name;
          return (
            <Button variant="link" onClick={() => setDetailsParam('policy', name)}>
              {name}
            </Button>
          );
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Policy Name" />,
        id: 'Policy Name',
        sortingFn: (rowA, rowB) => {
          return rowA.original.name.localeCompare(rowB.original.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
      },
      {
        accessorKey: 'action',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
      },
      {
        accessorKey: 'actionLabel',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Action Label" />,
        id: 'Action Label',
      },
      {
        accessorKey: 'audienceHref',
        cell: ({ cell }) => {
          const audienceHref = cell.row.original.audienceHref;
          return (
            <Button variant="link" onClick={() => setDetailsParam('audience', audienceHref)}>
              {audienceHref}
            </Button>
          );
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="Audience Href" />,
        id: 'Audience Href',
      },
    ],
    [loading, setDetailsParam, handleOnConfirm],
  );

  return (
    <section className="flex flex-1 flex-col overflow-hidden" data-testid="viewing-policies">
      <h2 className="mb-4 font-bold">Viewing Policies</h2>
      {mediaPolicy.viewingPolicies?.length ? (
        <DataTable
          className="h-auto [&_tbody_tr_td]:p-4"
          columns={columns}
          data={mediaPolicy.viewingPolicies}
          pagination={false}>
          <AddPolicyDialog mediaPolicy={mediaPolicy} />
        </DataTable>
      ) : (
        <div className="flex h-96 flex-col items-center justify-center gap-4 border border-dashed">
          <div className="text-center">
            <h2 className="mb-1 font-bold">No Policies</h2>
            <p className="text-sm">There are no viewing policies for this media</p>
          </div>
        </div>
      )}
    </section>
  );
}
